import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { usePlanSettings, useUpdatePlanSettings, type PlanSetting } from '@/billing/settingsApi'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

type Val = boolean | number | null

const GROUPS: { id: 'general' | 'free' | 'premium'; keys: string[] }[] = [
  {
    id: 'general',
    keys: [
      'plans.enforcement_enabled',
      'plans.anti_flood_enabled',
      'plans.message_burst_without_reply',
      'plans.silent_thread_auto_release_hours',
    ],
  },
  {
    id: 'free',
    keys: [
      'plans.plans.free.daily_quotas.deck_view',
      'plans.plans.free.daily_quotas.proactive_like',
      'plans.plans.free.daily_quotas.new_conversation',
      'plans.plans.free.daily_quotas.message_in_conversation',
      'plans.plans.free.state_limits.open_silent_threads',
    ],
  },
  {
    id: 'premium',
    keys: [
      'plans.plans.premium.daily_quotas.deck_view',
      'plans.plans.premium.daily_quotas.proactive_like',
      'plans.plans.premium.daily_quotas.new_conversation',
      'plans.plans.premium.daily_quotas.message_in_conversation',
      'plans.plans.premium.state_limits.open_silent_threads',
    ],
  },
]

function toVal(setting: PlanSetting): Val {
  if (setting.type === 'bool') return Boolean(setting.value)
  if (setting.value === null || setting.value === undefined) return null
  return Number(setting.value)
}

function metricKey(key: string): string {
  return key.split('.').pop() ?? key
}

/**
 * `/billing-settings` — admin-szerkeszthető sáv-katalógus (DEC-BILLING-007).
 * A kvóták + flagek (config/plans.php fölé DB-override). Csak a változott
 * mezők mennek mentésre. Operations surface.
 */
export function PlanSettingsForm() {
  const { t } = useTranslation()
  const query = usePlanSettings()
  const update = useUpdatePlanSettings()

  // Only the user's edits are tracked; the displayed value falls back to the
  // server value (toVal). This avoids a useEffect+setState data-mirror (which
  // the react-hooks lint flags) and keeps the diff-on-save trivial.
  const [edits, setEdits] = useState<Record<string, Val>>({})
  const [saved, setSaved] = useState(false)

  const settings = query.data ?? []
  const byKey = new Map<string, PlanSetting>(settings.map((s) => [s.key, s]))

  function valueFor(setting: PlanSetting): Val {
    const edited = edits[setting.key]
    return edited !== undefined ? edited : toVal(setting)
  }

  function setVal(key: string, value: Val) {
    setSaved(false)
    setEdits((e) => ({ ...e, [key]: value }))
  }

  function onSave() {
    const changes: Record<string, unknown> = {}
    for (const setting of settings) {
      const edited = edits[setting.key]
      if (edited !== undefined && edited !== toVal(setting)) {
        changes[setting.key] = edited
      }
    }
    if (Object.keys(changes).length === 0) return
    update.mutate(changes, {
      onSuccess: () => {
        setEdits({})
        setSaved(true)
      },
    })
  }

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <Link to="/" className="text-sm text-primary underline-offset-4 hover:underline self-start">
          {t('billingSettings.back')}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t('billingSettings.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('billingSettings.intro')}</p>
      </header>

      {query.isPending && <p className="text-muted-foreground">{t('common.loading')}</p>}

      {query.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('billingSettings.loadError')}</AlertDescription>
        </Alert>
      )}

      {update.isError && (
        <Alert variant="destructive">
          <AlertDescription>{t('billingSettings.saveError')}</AlertDescription>
        </Alert>
      )}

      {saved && (
        <Alert>
          <AlertDescription>{t('billingSettings.saved')}</AlertDescription>
        </Alert>
      )}

      {!query.isPending && !query.isError && (
        <div className="flex flex-col gap-6">
          {GROUPS.map((group) => (
            <section key={group.id} className="flex flex-col gap-3 rounded-lg border p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t(`billingSettings.group.${group.id}`)}
              </h2>
              <ul className="flex flex-col gap-2">
                {group.keys.map((key) => {
                  const setting = byKey.get(key)
                  if (!setting) return null
                  const value = valueFor(setting)
                  return (
                    <li key={key} className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm">
                        {t(`billingSettings.metric.${metricKey(key)}`)}
                        {setting.overridden && (
                          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                            {t('billingSettings.overridden')}
                          </span>
                        )}
                      </span>
                      {setting.type === 'bool' ? (
                        <input
                          type="checkbox"
                          aria-label={t(`billingSettings.metric.${metricKey(key)}`)}
                          checked={value === true}
                          onChange={(e) => setVal(key, e.target.checked)}
                          className="h-4 w-4"
                        />
                      ) : (
                        <input
                          type="number"
                          min={0}
                          aria-label={t(`billingSettings.metric.${metricKey(key)}`)}
                          placeholder={
                            setting.type === 'int_nullable' ? t('billingSettings.unlimited') : ''
                          }
                          value={value === null ? '' : String(value)}
                          onChange={(e) =>
                            setVal(
                              key,
                              e.target.value === ''
                                ? setting.type === 'int_nullable'
                                  ? null
                                  : 0
                                : Number(e.target.value),
                            )
                          }
                          className="w-28 rounded border px-2 py-1 text-sm"
                        />
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}

          <Button type="button" onClick={onSave} disabled={update.isPending} className="self-start">
            {t('billingSettings.save')}
          </Button>
        </div>
      )}
    </main>
  )
}
